import {
    BiddingLadder,
    BonusIndicator,
    BridgePosition,
    Call,
    Card,
    CardPosition,
    Cards,
    SeatData,
    Speed,
    Trick,
    ranks,
    suits,
} from './types';
import { SFS_HAND_PROPS, getCardId } from '../utils/mixed';
import { environment } from '../env';

export const defaultSeatData: SeatData = {
    background: undefined,
    bridgePosition: undefined,
    bridgeWarAvatar: undefined,
    cardWidthMultiplier: 1,
    dummyCards: 0,
    dynamicCardColumnsWidth: false,
    isHighlighted: false,
    isInteractive: false,
    isMe: false,
    isVertical: false,
    isVisible: false,
    labelPrimary: undefined,
    labelSecondary: undefined,
    labelTertiary: undefined,
    seatPosition: undefined,
    sfsProp: SFS_HAND_PROPS.north,
    size: 'm',
};

export const defaultCard: Omit<Card, 'id' | 'rank' | 'suit'> = {
    bridgePosition: undefined,
    cardPosition: CardPosition.DECK,
    highlighted: false,
    raised: false,
    suitSymbol: false,
    visible: undefined,
};

export const defaultCards = (): Cards => {
    return suits.reduce((cards, suit) => {
        return ranks.reduce((cards, rank) => {
            const id = getCardId(suit, rank);
            return {
                ...cards,
                [id]: {
                    ...defaultCard,
                    id,
                    rank,
                    suit,
                },
            };
        }, cards);
    }, {} as Cards);
};

export const cardBackgrounds: string[] = ['images/cardback_blue.png', 'images/cardback_red.png'];

const audreyCalls: Call[] = [
    '7nt',
    '7s',
    '7h',
    '7d',
    '7c',
    '6nt',
    '6s',
    '6h',
    '6d',
    '6c',
    '5nt',
    '5s',
    '5h',
    '5d',
    '5c',
    '4nt',
    '4s',
    '4h',
    '4d',
    '4c',
    '3nt',
    '3s',
    '3h',
    '3d',
    '3c',
    '2nt',
    '2s',
    '2h',
    '2d',
    '2c',
    '1nt',
    '1s',
    '1h',
    '1d',
    '1c',
];

export const sharkCalls: Call[] = [
    '1nt',
    '1s',
    '1h',
    '1d',
    '1c',
    '2nt',
    '2s',
    '2h',
    '2d',
    '2c',
    '3nt',
    '3s',
    '3h',
    '3d',
    '3c',
    '4nt',
    '4s',
    '4h',
    '4d',
    '4c',
    '5nt',
    '5s',
    '5h',
    '5d',
    '5c',
    '6nt',
    '6s',
    '6h',
    '6d',
    '6c',
    '7nt',
    '7s',
    '7h',
    '7d',
    '7c',
];

export const calls = environment === 'audrey' ? audreyCalls : sharkCalls;

export const defaultBiddingLadder: BiddingLadder = {
    bids: calls.map((call) => ({
        call,
        backgroundColor: undefined,
    })),
    bonusIndicators: [],
    firstVisibleCall: '1c',
    isActive: true,
    isVisible: false,
    minimalValidCall: '1c',
    showArrow: true,
    showBonusIndicator: true,
    stake: {
        call: 'xdbl',
        invisible: false,
        backgroundColor: undefined,
        disabled: true,
    },
    pass: {
        invisible: false,
        backgroundColor: undefined,
        disabled: false,
    },
};

export const allBonusIndicators: Record<BonusIndicator, Call[]> = {
    [BonusIndicator.g]: ['7c', '7d', '7h', '7s', '7nt'],
    [BonusIndicator.j]: ['3nt', '4h', '4s'],
    [BonusIndicator.m]: ['5c', '5d'],
    [BonusIndicator.s]: ['6c', '6d', '6h', '6s', '6nt'],
};

export const emptyTrick: Trick = {
    cardIds: [],
    winner: undefined,
};

export const playCardAnimationSpeed: Record<Speed, number> = {
    [Speed.verySlow]: 1000,
    [Speed.slow]: 600,
    [Speed.medium]: 400,
    [Speed.fast]: 200,
    [Speed.veryFast]: 0,
    [Speed.instant]: 0,
};

export const claimTrickAnimationSpeed: Record<Speed, number> = {
    [Speed.verySlow]: 1200,
    [Speed.slow]: 800,
    [Speed.medium]: 600,
    [Speed.fast]: 400,
    [Speed.veryFast]: 0,
    [Speed.instant]: 0,
};

export const speeds: Map<Speed, string> = new Map<Speed, string>([
    [Speed.verySlow, 'Very Slow'],
    [Speed.slow, 'Slow'],
    [Speed.medium, 'Medium'],
    [Speed.fast, 'Fast'],
    [Speed.veryFast, 'Very Fast'],
]);

const _bridgePos: BridgePosition[] = [
    BridgePosition.north,
    BridgePosition.east,
    BridgePosition.south,
    BridgePosition.west,
];
export function bridgePos(p: number) {
    return p < 0 || p > 3 ? BridgePosition.south : _bridgePos[p];
}

export const numBridgePos: Record<BridgePosition, number> = {
    [BridgePosition.north]: 0,
    [BridgePosition.east]: 1,
    [BridgePosition.south]: 2,
    [BridgePosition.west]: 3,
};

export const bridgeWarTitleMap: Record<string, string> = {
    bwar_7_: 'BridgeWar™ <span class="nowrap">Level 1</span>',
    bwar_7_e_: 'BridgeWar™ Classic',
    bwar_8_: 'BridgeWar™ Heroes <span class="nowrap">Level 1</span>',
    bwar_8_e_: 'BridgeWar™ Heroes',
    bwar_c_: 'BridgeWar™ Select',
    bwar_ex_: 'BridgeWar™ Extreme',
};
